import React, { useState, useEffect, useMemo, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { dataService } from '../../services/dataService';
import { Class, UserRole } from '../../data/types';
import Avatar from '../ui/Avatar';

interface SidebarProps {
  activeItem: string;
  onItemClick: (item: string) => void;
  userRole?: UserRole;
  userName: string;
  userAvatar?: string;
  onLogout: () => void;
  isCollapsed: boolean;
  onToggleSidebar: () => void;
}

interface SidebarItem {
  id: string;
  name: string;
  icon: React.ReactNode;
  subitems?: {
    id: string;
    name: string;
    isSelected?: boolean;
  }[];
}

const Sidebar: React.FC<SidebarProps> = ({ 
  activeItem, 
  onItemClick, 
  userRole,
  userName,
  userAvatar,
  onLogout,
  isCollapsed,
  onToggleSidebar
}) => {
  
  // State management
  const [expandedItems, setExpandedItems] = useState<string[]>(['classes']); // Start with classes expanded
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  
  // Refs for handling click outside
  const notificationRef = useRef<HTMLDivElement>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  // Extract class ID from active item if it's a class item
  useEffect(() => {
    if (activeItem && activeItem.startsWith('classes-')) {
      const classId = activeItem.replace('classes-', '');
      setSelectedClassId(classId);
      
      // Make sure classes is in expanded items
      if (!expandedItems.includes('classes')) {
        setExpandedItems(prev => [...prev, 'classes']);
      }
    }
  }, [activeItem, expandedItems]);
  
  // Listen for class selection from dropdown in ClassDetailsScreen
  useEffect(() => {
    const handleClassSelection = (event: CustomEvent) => {
      const { classId } = event.detail;
      setSelectedClassId(classId);
      onItemClick(`classes-${classId}`);
      
      // Make sure classes is in expanded items
      if (!expandedItems.includes('classes')) {
        setExpandedItems(prev => [...prev, 'classes']);
      }
    };
    
    window.addEventListener('classSelected', handleClassSelection as EventListener);
    
    return () => {
      window.removeEventListener('classSelected', handleClassSelection as EventListener);
    };
  }, [expandedItems, onItemClick]);

  // Fetch classes from data service
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoading(true);
        const classesData = await dataService.getClasses();
        // Filter classes by teacher ID - in a real app, this would be the current user's ID
        // For now, we'll just use teacher-001 as an example
        const teacherClasses = classesData.filter(cls => cls.teacherId === 'teacher-001');
        setClasses(teacherClasses);
        
        // If we have a URL with a class but no selectedClassId yet, set it from the URL
        if (!selectedClassId && window.location.pathname.includes('/classes/')) {
          const pathParts = window.location.pathname.split('/');
          const classIdFromUrl = pathParts[pathParts.length - 1];
          if (classIdFromUrl) {
            setSelectedClassId(classIdFromUrl);
          }
        }
      } catch (error) {
        console.error('Error fetching classes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchClasses();
  }, [selectedClassId]);
  
  // Add click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close notifications dropdown if clicked outside
      if (
        notificationsOpen && 
        notificationRef.current && 
        !notificationRef.current.contains(event.target as Node)
      ) {
        setNotificationsOpen(false);
      }
      
      // Close user dropdown if clicked outside
      if (
        dropdownOpen && 
        userDropdownRef.current && 
        !userDropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    
    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationsOpen, dropdownOpen]);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
    if (notificationsOpen) setNotificationsOpen(false);
  };

  const toggleNotifications = () => {
    setNotificationsOpen(!notificationsOpen);
    if (dropdownOpen) setDropdownOpen(false);
  };

  const toggleItem = (itemId: string) => {
    setExpandedItems(prevItems =>
      prevItems.includes(itemId)
        ? prevItems.filter(id => id !== itemId)
        : [...prevItems, itemId]
    );
  };

  // Define sidebarItems with useMemo to prevent it from being recreated on every render
  const sidebarItems = useMemo<SidebarItem[]>(() => {
    // Handle undefined userRole by defaulting to Teacher
    const role = userRole || 'Teacher';
    
    // Explicitly handle each role case to ensure correct menu items are displayed
    // This is a more direct approach to fix the issue
    const roleStr = String(role).trim();
    
    // Super Admin sidebar items
    if (roleStr === 'SuperAdmin') {
      return [
        {
          id: 'dashboard',
          name: 'Dashboard',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"></path>
              <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"></path>
            </svg>
          ),
        },
        {
          id: 'districts',
          name: 'Districts',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"></path>
            </svg>
          ),
        },
        {
          id: 'schools',
          name: 'Schools',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"></path>
            </svg>
          ),
        },
        {
          id: 'classes',
          name: 'Classes',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"></path>
            </svg>
          ),
        },
        {
          id: 'games',
          name: 'Games',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"></path>
            </svg>
          ),
        },
        {
          id: 'questions',
          name: 'Questions',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"></path>
            </svg>
          ),
        },
        {
          id: 'standards',
          name: 'Standards',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
          ),
        },
        {
          id: 'learning-paths',
          name: 'Learning Paths',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M12 1.586l-4 4v12.828l4-4V1.586zM3.707 3.293A1 1 0 002 4v10a1 1 0 00.293.707L6 18.414V5.586L3.707 3.293zM17.707 5.293L14 1.586v12.828l2.293 2.293A1 1 0 0018 16V6a1 1 0 00-.293-.707z"
                clipRule="evenodd"
              ></path>
            </svg>
          ),
          subitems: [
            { id: 'superadmin-learning-paths', name: 'Manage Paths' },
            { id: 'superadmin-learning-paths-create', name: 'Create New Path' }
          ],
        },
        {
          id: 'analytics',
          name: 'Analytics',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"></path>
            </svg>
          ),
        },
        {
          id: 'users',
          name: 'Users',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"></path>
            </svg>
          ),
        },
        {
          id: 'system-config',
          name: 'System',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"></path>
            </svg>
          ),
        },
      ];
    }
    // School Admin sidebar items
    else if (roleStr === 'SchoolAdmin') {
      return [
        {
          id: 'schooladmin/dashboard',
          name: 'Dashboard',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"></path>
              <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"></path>
            </svg>
          ),
        },
        {
          id: 'schooladmin/classes',
          name: 'Classes',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"></path>
            </svg>
          ),
        },
        {
          id: 'schooladmin/games',
          name: 'Games',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"></path>
            </svg>
          ),
        },
        {
          id: 'schooladmin/questions',
          name: 'Questions',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"></path>
            </svg>
          ),
        },
        {
          id: 'schooladmin/standards',
          name: 'Standards',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
          ),
        },
        {
          id: 'schooladmin/learning-paths',
          name: 'Learning Paths',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M12 1.586l-4 4v12.828l4-4V1.586zM3.707 3.293A1 1 0 002 4v10a1 1 0 00.293.707L6 18.414V5.586L3.707 3.293zM17.707 5.293L14 1.586v12.828l2.293 2.293A1 1 0 0018 16V6a1 1 0 00-.293-.707z"
                clipRule="evenodd"
              ></path>
            </svg>
          ),
          subitems: [
            { id: 'schooladmin/learning-paths', name: 'Manage Paths' },
            { id: 'schooladmin/learning-paths/create', name: 'Create New Path' }
          ],
        },
        {
          id: 'schooladmin/analytics',
          name: 'Analytics',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"></path>
            </svg>
          ),
        },
        {
          id: 'schooladmin/users',
          name: 'Users',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"></path>
            </svg>
          ),
        },
        {
          id: 'schooladmin/system-config',
          name: 'System',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"></path>
            </svg>
          ),
        },
      ];
    }
    // Admin sidebar items
    else if (roleStr === 'Admin') {
      return [
        {
          id: 'dashboard',
          name: 'Dashboard',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"></path>
              <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"></path>
            </svg>
          ),
        },
        {
          id: 'schools',
          name: 'Schools',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"></path>
            </svg>
          ),
        },
        {
          id: 'teachers',
          name: 'Teachers',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"></path>
            </svg>
          ),
        },
        {
          id: 'students',
          name: 'Students',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"></path>
            </svg>
          ),
        },
        {
          id: 'learning-paths',
          name: 'Learning Paths',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M12 1.586l-4 4v12.828l4-4V1.586zM3.707 3.293A1 1 0 002 4v10a1 1 0 00.293.707L6 18.414V5.586L3.707 3.293zM17.707 5.293L14 1.586v12.828l2.293 2.293A1 1 0 0018 16V6a1 1 0 00-.293-.707z"
                clipRule="evenodd"
              ></path>
            </svg>
          ),
          subitems: [
            { id: 'admin-learning-paths', name: 'Manage Paths' },
            { id: 'admin-learning-paths-create', name: 'Create New Path' },
            { id: 'admin-learning-paths-assign', name: 'Assign Paths' },
          ],
        },
        {
          id: 'standards',
          name: 'Standards',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"></path>
              <path
                fillRule="evenodd"
                d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                clipRule="evenodd"
              ></path>
            </svg>
          ),
        },
        {
          id: 'reports',
          name: 'Reports',
          icon: (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"></path>
            </svg>
          ),
          subitems: [
            { id: 'reports-district-progress', name: 'District Progress' },
            { id: 'reports-school-performance', name: 'School Performance' },
            { id: 'reports-standards-mastery', name: 'Standards Mastery' },
          ],
        },
      ];
    }
    
    // Teacher sidebar items (default fallback)
    else {
      return [
    {
      id: 'dashboard',
      name: 'Dashboard',
      icon: (
        <svg
          className="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"></path>
          <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"></path>
        </svg>
      ),
    },
    {
      id: 'classes',
      name: 'My Classes',
      icon: (
        <svg
          className="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"></path>
        </svg>
      ),
    },
    {
      id: 'students',
      name: 'My Students',
      icon: (
        <svg
          className="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"></path>
        </svg>
      ),
    },
    {
      id: 'student-groups',
      name: 'Student Groups',
      icon: (
        <svg
          className="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"></path>
        </svg>
      ),
    },
    {
      id: 'learning-paths',
      name: 'Learning Paths',
      icon: (
        <svg
          className="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            d="M12 1.586l-4 4v12.828l4-4V1.586zM3.707 3.293A1 1 0 002 4v10a1 1 0 00.293.707L6 18.414V5.586L3.707 3.293zM17.707 5.293L14 1.586v12.828l2.293 2.293A1 1 0 0018 16V6a1 1 0 00-.293-.707z"
            clipRule="evenodd"
          ></path>
        </svg>
      ),
      subitems: [
        { id: 'learning-paths-create', name: 'Create New Path' },
        { id: 'learning-paths-manage', name: 'Manage Paths' },
        { id: 'learning-paths-assign', name: 'Assign Paths' },
      ],
    },
    {
      id: 'standards',
      name: 'Standards',
      icon: (
        <svg
          className="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"></path>
          <path
            fillRule="evenodd"
            d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
            clipRule="evenodd"
          ></path>
        </svg>
      ),
    },
    {
      id: 'games',
      name: 'Games',
      icon: (
        <svg
          className="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M11 17a1 1 0 001.447.894l4-2A1 1 0 0017 15V9.236a1 1 0 00-1.447-.894l-4 2a1 1 0 00-.553.894V17zM15.211 6.276a1 1 0 000-1.788l-4.764-2.382a1 1 0 00-.894 0L4.789 4.488a1 1 0 000 1.788l4.764 2.382a1 1 0 00.894 0l4.764-2.382zM4.447 8.342A1 1 0 003 9.236V15a1 1 0 00.553.894l4 2A1 1 0 009 17v-5.764a1 1 0 00-.553-.894l-4-2z"></path>
        </svg>
      ),
    },
    {
      id: 'reports',
      name: 'Reports',
      icon: (
        <svg
          className="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"></path>
        </svg>
      ),
      subitems: [
        { id: 'reports-class-progress', name: 'Class Progress' },
        { id: 'reports-student-performance', name: 'Student Performance' },
        { id: 'reports-standards-mastery', name: 'Standards Mastery' },
      ],
    },
    {
      id: 'resources',
      name: 'Resources',
      icon: (
        <svg
          className="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
            clipRule="evenodd"
          ></path>
        </svg>
      ),
    },
  ];
    }
  }, [userRole, selectedClassId, classes, loading]); // Include userRole, selectedClassId, classes and loading in the dependency array

  // Auto-expand parent menu when a submenu item is active
  React.useEffect(() => {
    if (activeItem) {
      // Find the parent item that contains this activeItem as a subitem
      const parentItem = sidebarItems.find(item => 
        item.subitems?.some(subitem => subitem.id === activeItem)
      );
      
      if (parentItem && !expandedItems.includes(parentItem.id)) {
        setExpandedItems(prev => [...prev, parentItem.id]);
      }
      
      // If activeItem is a class, make sure classes menu is expanded
      if (activeItem.startsWith('classes-') && !expandedItems.includes('classes')) {
        setExpandedItems(prev => [...prev, 'classes']);
      }
    }
  }, [activeItem, expandedItems, sidebarItems]);

  return (
    <aside
      className={`h-screen transition-all duration-300 bg-white border-r border-gray-200 ${isCollapsed ? 'w-16' : 'w-64'}`}
      aria-label="Sidebar"
    >
      <div className="h-full flex flex-col">
        {/* Logo Section */}
        <div className="relative border-b border-gray-200">
          <NavLink to="/" className="flex items-center p-4 pl-5">
            <img
              className={`h-12 w-auto object-contain ${isCollapsed ? 'mx-auto' : ''}`}
              src="/assets/images/swunmath_logo.png"
              alt="SwunMath Logo"
            />
          </NavLink>
          {/* Toggle button at top right */}
          <button
            type="button"
            className="absolute top-4 right-2 p-1 text-gray-500 rounded-lg hover:text-primary hover:bg-gray-100"
            onClick={onToggleSidebar}
          >
            <span className="sr-only">Toggle sidebar</span>
            {isCollapsed ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M15.41 16.59L10.83 12 15.41 7.41 14 6l-6 6 6 6 1.41-1.41z"/>
              </svg>
            )}
          </button>
        </div>

        {/* Main Menu Section */}
        <div className="flex-1 overflow-y-auto px-3 py-4">
          <ul className="space-y-2">
            {sidebarItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={
                    item.subitems
                      ? () => toggleItem(item.id)
                      : () => onItemClick(item.id)
                  }
                  className={`${
                    activeItem === item.id ? 'bg-primary-50 text-primary'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-primary'
                  } ${
                    isCollapsed ? 'justify-center px-2' : 'justify-between px-3'
                  } flex items-center w-full py-2.5 text-sm font-medium rounded-lg transition duration-75 group`}
                >
                  <div className="flex items-center">
                    <span className={activeItem === item.id ? 'text-primary' : 'text-gray-500'}>
                      {item.icon}
                    </span>
                    {!isCollapsed && (
                      <span className="ml-3">{item.name}</span>
                    )}
                  </div>
                  {!isCollapsed && item.subitems && (
                    <svg
                      className={`w-4 h-4 transition-transform ${
                        expandedItems.includes(item.id) ? 'rotate-180' : ''
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  )}
                </button>
                {!isCollapsed && item.subitems && expandedItems.includes(item.id) && (
                  <ul className="mt-1 space-y-1">
                    {item.subitems.map((subitem) => (
                      <li key={subitem.id}>
                        <button
                          onClick={() => onItemClick(subitem.id)}
                          className={`${
                            activeItem === subitem.id || subitem.isSelected
                              ? 'bg-primary-50 text-primary'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-primary'
                          } flex items-center w-full py-2 px-3 pl-10 text-sm font-medium transition duration-75 rounded-lg group`}
                        >
                          {subitem.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom Section with Notifications and User Profile */}
        <div className="border-t border-gray-200">
          {/* Notifications Row */}
          <div className="py-2 px-3 border-b border-gray-200">
            <div className="relative" ref={notificationRef}>
              <button
                onClick={toggleNotifications}
                className="flex items-center w-full rounded-lg px-2 py-2 hover:bg-gray-100 transition-colors"
              >
                <div className="relative">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
                  </svg>
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-red-500 rounded-full">3</span>
                </div>
                {!isCollapsed && (
                  <span className="ml-3 text-sm font-medium text-gray-700">Notifications</span>
                )}
              </button>
              
              {notificationsOpen && !isCollapsed && (
                <div className="absolute left-full bottom-0 ml-2 w-80 bg-white rounded-lg shadow-lg z-10 border border-gray-100">
                  <div className="p-3 border-b border-gray-100">
                    <h3 className="text-sm font-medium text-gray-700">Notifications</h3>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    <a href="#" className="flex px-4 py-3 hover:bg-gray-50 border-b border-gray-100">
                      <div className="flex-shrink-0">
                        <span className="flex h-8 w-8 rounded-full bg-primary-100 items-center justify-center">
                          <svg className="h-4 w-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"></path>
                          </svg>
                        </span>
                      </div>
                      <div className="ml-3 w-0 flex-1">
                        <p className="text-sm text-gray-700">Assignment due tomorrow</p>
                        <p className="text-xs text-gray-500 mt-1">10 minutes ago</p>
                      </div>
                    </a>
                    <a href="#" className="flex px-4 py-3 hover:bg-gray-50 border-b border-gray-100">
                      <div className="flex-shrink-0">
                        <span className="flex h-8 w-8 rounded-full bg-blue-100 items-center justify-center">
                          <svg className="h-4 w-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"></path>
                            <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z"></path>
                          </svg>
                        </span>
                      </div>
                      <div className="ml-3 w-0 flex-1">
                        <p className="text-sm text-gray-700">New message from Sarah</p>
                        <p className="text-xs text-gray-500 mt-1">1 hour ago</p>
                      </div>
                    </a>
                  </div>
                  <div className="p-2 border-t border-gray-100">
                    <a href="#" className="flex justify-center items-center p-2 text-sm font-medium text-primary rounded-lg hover:bg-gray-50">
                      View all notifications
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* User Profile Row */}
          <div className="py-2 px-3">
            <div className="relative" ref={userDropdownRef}>
              <div className="flex items-center justify-between w-full rounded-lg px-2 py-2 hover:bg-gray-100 transition-colors">
                <button
                  onClick={toggleDropdown}
                  className="flex items-center flex-1"
                >
                  <Avatar
                    src={userAvatar || '/assets/images/default-avatar.png'}
                    alt={userName}
                    size={isCollapsed ? 'sm' : 'md'}
                  />
                  {!isCollapsed && (
                    <div className="ml-3 text-left">
                      <p className="text-sm font-medium text-gray-700">{userName}</p>
                      <p className="text-xs text-gray-500">{userRole}</p>
                    </div>
                  )}
                </button>
                
                {/* Three dots menu icon */}
                {!isCollapsed && (
                  <button 
                    onClick={toggleDropdown}
                    className="p-1 rounded-full hover:bg-gray-200 text-gray-500"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                    </svg>
                  </button>
                )}
              </div>
              
              {dropdownOpen && (
                <div className={`absolute ${isCollapsed ? 'left-full ml-2' : 'right-0'} bottom-full mb-2 w-56 bg-white rounded-lg shadow-lg overflow-hidden z-10 border border-gray-100`}>
                  <div className="py-2">
                    <a href="/profile" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"></path>
                      </svg>
                      Profile
                    </a>
                    <a href="/settings" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"></path>
                      </svg>
                      Settings
                    </a>
                    <a href="/change-password" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"></path>
                      </svg>
                      Change Password
                    </a>
                    <button 
                      onClick={onLogout}
                      className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V4a1 1 0 00-1-1H3zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd"></path>
                      </svg>
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;